import { useState, useEffect, useCallback } from "react";
import { Loader2, RefreshCw, Trash2, GraduationCap, Calendar, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin panel to view public form submissions:
 *  - Training Requests (POST /api/training/request)
 *  - Community Fair Interests (POST /api/community-fair/interest)
 *
 * Volunteer applications are visible in the existing "Careers" panel because
 * they're stored in db.applications with vacancy_title = "General Volunteer".
 */
export const AdminPublicFormsPanel = ({ getAuthHeader }) => {
  const [tab, setTab] = useState("training");
  const [trainingReqs, setTrainingReqs] = useState([]);
  const [fairInterests, setFairInterests] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tr, fi] = await Promise.all([
        axios.get(`${API}/admin/training-requests`, getAuthHeader()),
        axios.get(`${API}/admin/fair-interests`, getAuthHeader()),
      ]);
      setTrainingReqs(tr.data);
      setFairInterests(fi.data);
    } catch {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (collection, id, status) => {
    try {
      await axios.patch(`${API}/admin/${collection}/${id}`, { status }, getAuthHeader());
      toast.success("Status updated");
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const remove = async (collection, id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await axios.delete(`${API}/admin/${collection}/${id}`, getAuthHeader());
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const count = { training: trainingReqs.length, fair: fairInterests.length };

  return (
    <div className="space-y-4" data-testid="public-forms-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          <TabBtn active={tab === "training"} onClick={() => setTab("training")} testId="pf-tab-training">
            <GraduationCap className="w-4 h-4 mr-1.5" /> Training Requests
            <span className="ml-1.5 text-xs bg-warm-100 text-warm-800 px-1.5 py-0.5 rounded-full">{count.training}</span>
          </TabBtn>
          <TabBtn active={tab === "fair"} onClick={() => setTab("fair")} testId="pf-tab-fair">
            <Calendar className="w-4 h-4 mr-1.5" /> Resource Fair Interests
            <span className="ml-1.5 text-xs bg-warm-100 text-warm-800 px-1.5 py-0.5 rounded-full">{count.fair}</span>
          </TabBtn>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="pf-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-gray-500">
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-warm-700" />
        </div>
      ) : tab === "training" ? (
        <TrainingTable rows={trainingReqs} onStatus={(id, s) => updateStatus("training-requests", id, s)} onDelete={(id) => remove("training-requests", id)} />
      ) : (
        <FairTable rows={fairInterests} onStatus={(id, s) => updateStatus("fair-interests", id, s)} onDelete={(id) => remove("fair-interests", id)} />
      )}
    </div>
  );
};

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        active ? "bg-warm-700 text-white" : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
      }`}
    >
      {children}
    </button>
  );
}

const STATUS_OPTIONS = ["new", "contacted", "scheduled", "completed", "declined"];

function StatusPill({ value }) {
  const colors = {
    new: "bg-blue-100 text-blue-800",
    contacted: "bg-amber-100 text-amber-800",
    scheduled: "bg-purple-100 text-purple-800",
    completed: "bg-green-100 text-green-800",
    declined: "bg-gray-100 text-gray-600",
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full ${colors[value] || "bg-gray-100 text-gray-700"}`}>{value}</span>;
}

function ActionCell({ row, onStatus, onDelete, collection }) {
  return (
    <div className="flex items-center gap-1">
      <select
        value={row.status}
        onChange={(e) => onStatus(row.id, e.target.value)}
        className="text-xs border border-gray-200 rounded px-1.5 py-1"
        data-testid={`pf-status-${collection}-${row.id}`}
      >
        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <button
        onClick={() => onDelete(row.id)}
        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
        title="Delete"
        data-testid={`pf-delete-${collection}-${row.id}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function TrainingTable({ rows, onStatus, onDelete }) {
  if (!rows.length) return <EmptyState label="No training requests yet." />;
  return (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wider">
            <tr>
              <Th>Received</Th><Th>Organization</Th><Th>Contact</Th><Th>Audience</Th>
              <Th>Attendees</Th><Th>Date / Mode</Th><Th>Status</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`pf-tr-row-${r.id}`}>
                <Td>{formatDate(r.created_at)}</Td>
                <Td className="font-medium text-gray-900">{r.organization}</Td>
                <Td>
                  <div>{r.contact_name}</div>
                  <div className="text-xs text-gray-500"><a href={`mailto:${r.email}`} className="hover:underline">{r.email}</a></div>
                  {r.phone && <div className="text-xs text-gray-500">{r.phone}</div>}
                </Td>
                <Td className="text-xs">{r.audience_type || "—"}</Td>
                <Td className="text-xs">{r.attendee_count || "—"}</Td>
                <Td className="text-xs">
                  <div>{r.preferred_date || "—"}</div>
                  <div className="text-gray-500">{r.delivery_mode || "—"}</div>
                </Td>
                <Td><StatusPill value={r.status} /></Td>
                <Td><ActionCell row={r} onStatus={onStatus} onDelete={onDelete} collection="tr" /></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.some((r) => r.training_goals || r.additional_comments) && (
        <div className="border-t border-gray-100 p-4 space-y-3 bg-gray-50 max-h-96 overflow-y-auto">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Details</p>
          {rows.filter((r) => r.training_goals || r.additional_comments).map((r) => (
            <div key={r.id} className="text-xs bg-white border border-gray-100 rounded p-3">
              <p className="font-semibold text-gray-800 mb-1">{r.organization} — {r.contact_name}</p>
              {r.training_goals && <p><span className="text-gray-500">Goals:</span> {r.training_goals}</p>}
              {r.additional_comments && <p><span className="text-gray-500">Comments:</span> {r.additional_comments}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FairTable({ rows, onStatus, onDelete }) {
  if (!rows.length) return <EmptyState label="No community fair interests yet." />;
  const labelFor = {
    resource_partner: "Resource partner",
    sponsor: "Sponsor",
    volunteer: "Volunteer",
    attendee: "Attendee",
    in_kind: "In-kind donation",
  };
  return (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wider">
            <tr>
              <Th>Received</Th><Th>Contact</Th><Th>Organization</Th><Th>Interest</Th>
              <Th>Notes</Th><Th>Status</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`pf-fair-row-${r.id}`}>
                <Td>{formatDate(r.created_at)}</Td>
                <Td>
                  <div className="font-medium text-gray-900">{r.contact_name}</div>
                  <div className="text-xs text-gray-500"><a href={`mailto:${r.email}`} className="hover:underline">{r.email}</a></div>
                  {r.phone && <div className="text-xs text-gray-500">{r.phone}</div>}
                </Td>
                <Td className="text-xs">{r.organization || "—"}</Td>
                <Td className="text-xs">{labelFor[r.interest_type] || r.interest_type}</Td>
                <Td className="text-xs max-w-xs truncate" title={r.notes}>{r.notes || "—"}</Td>
                <Td><StatusPill value={r.status} /></Td>
                <Td><ActionCell row={r} onStatus={onStatus} onDelete={onDelete} collection="fair" /></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-2 font-medium">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}
function EmptyState({ label }) {
  return (
    <div className="p-10 text-center text-gray-500 bg-white border border-gray-100 rounded-xl">
      <Check className="w-6 h-6 mx-auto mb-2 text-gray-300" />
      <p className="text-sm">{label}</p>
    </div>
  );
}
function formatDate(iso) {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
